/**
 * Purpose:
 *   Shared page-level state component for loading, error, and not-found shells.
 *
 * Notes:
 *   - Keeps visual structure reusable while callers own i18n copy and page actions.
 *   - Uses BlankState for non-loading visuals and a fixed Spinner treatment for loading.
 */

'use client';

import { BlankState } from '@/components/ui/blankState';
import { Button } from '@/components/ui/button/Button';
import { Spinner } from '@/components/ui/spinner/Spinner';
import { cx } from '@/util/functions/cx';
import type { IPageStateAction, IPageStateProps } from './PageState.types';
import { pageStateStyles as styles } from './PageState.styles';

export function PageState({
  state,
  children,
  content,
  actions,
  testIds,
  appearance,
}: IPageStateProps) {
  const mountedChildren = (
    <div
      className={
        state === 'ready' ? styles.contentWrapper : styles.hiddenContent
      }
      aria-hidden={state !== 'ready'}
    >
      {children}
    </div>
  );

  if (state === 'ready') {
    return mountedChildren;
  }

  if (state === 'loading') {
    return (
      <>
        {mountedChildren}
        <div
          className={cx(
            styles.container,
            styles.loadingContainer,
            appearance?.containerClassName
          )}
          role="status"
          aria-live="polite"
          data-testid={testIds?.loading}
        >
          <Spinner size={64} thickness={7} />
          <p
            className={cx(styles.loadingMessage, appearance?.messageClassName)}
          >
            {content.loadingMessage}
          </p>
        </div>
      </>
    );
  }

  const isError = state === 'error';
  const blankStateType =
    appearance?.blankStateType ?? (isError ? 'not-found' : 'error-404');
  const resolvedActions = [actions?.secondary, actions?.primary].filter(
    Boolean
  ) as IPageStateAction[];

  return (
    <>
      {mountedChildren}
      <div
        className={cx(
          styles.container,
          styles.blankContainer,
          appearance?.containerClassName
        )}
        data-testid={isError ? testIds?.error : testIds?.notFound}
      >
        <BlankState
          type={blankStateType}
          message={isError ? content.errorMessage : content.notFoundMessage}
          className={appearance?.blankStateClassName}
        />
        {resolvedActions.length > 0 ? (
          <div className={cx(styles.actions, appearance?.actionsClassName)}>
            {resolvedActions.map((action) => (
              <Button
                key={`${action.label}-${action.testId ?? 'action'}`}
                type="button"
                variant={action.variant ?? 'primary'}
                size="sm"
                onClick={action.onClick}
                dataTestId={action.testId}
              >
                {action.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
}
